<script lang="ts">
  /**
   * choice / reduce / complexity-quiz 共通（card-schema.md §3.4〜§3.6、judge.md §4.3、components.md §1.5）。
   * 単一選択はラジオ、複数選択はチェックボックス。1〜9 で選び、Enter で採点する。
   */
  import { gradeChoice } from '../../lib/judge';
  import CardShell from '../CardShell.svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import InlineMarkdown from '../InlineMarkdown.svelte';
  import type { GradeResult } from '../types';
  import type { ChoiceAnswer } from './answer';
  import {
    choiceItems,
    choiceMessage,
    constraintText,
    displayItems,
    isMultiple,
    predictionText,
    type AnyChoiceCard,
  } from './choice';

  interface Props {
    card: AnyChoiceCard;
    index: number;
    total: number;
    box?: number;
    result?: GradeResult | undefined;
    answer?: ChoiceAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    ongrade?: ((result: GradeResult, answer: ChoiceAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（level は開いた段階、1 始まり） */
    onhint?: ((level: number) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer: savedAnswer,
    showUseCase = false,
    globalKeys = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
  }: Props = $props();

  const uid = $props.id();
  const items = $derived(choiceItems(card));
  const shownItems = $derived(displayItems(card));
  const multiple = $derived(isMultiple(card));

  let selected = $state<number[]>([]);
  let gradedAnswer = $state<ChoiceAnswer | undefined>();
  const shown = $derived(gradedAnswer ?? savedAnswer);

  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    gradedAnswer = undefined;
    selected = savedAnswer ? [...savedAnswer.selected] : [];
  });

  const graded = $derived(result !== undefined);
  const gradeState = $derived(graded ? 'graded' : selected.length > 0 ? 'answering' : 'idle');

  const prompt = $derived(
    card.type === 'reduce' ? 'この問題は、どの手法で解けますか。' : card.question,
  );

  function choose(i: number) {
    if (graded) return;
    if (multiple) {
      selected = selected.includes(i) ? selected.filter((s) => s !== i) : [...selected, i];
    } else {
      selected = [i];
    }
  }

  function submit() {
    if (graded || selected.length === 0) return;
    const r = gradeChoice(items, multiple, selected);
    if (r === 'invalid') return;
    const message = choiceMessage(items, r, multiple);
    const a: ChoiceAnswer = {
      kind: 'choice',
      selected: [...selected].sort((x, y) => x - y),
      result: r,
    };
    gradedAnswer = a;
    ongrade?.(message, a);
  }

  let fieldset: HTMLFieldSetElement | undefined = $state();

  function onshortcut(e: KeyboardEvent): boolean {
    if (graded || e.shiftKey || !/^[1-9]$/.test(e.key)) return false;
    const item = shownItems[Number(e.key) - 1];
    if (!item) return false;
    choose(item.index);
    fieldset?.querySelector<HTMLInputElement>(`input[value="${item.index}"]`)?.focus();
    return true;
  }

  const chosenExplanations = $derived(
    graded && shown
      ? shown.selected.flatMap((i) => {
          const e = items[i]?.explanation;
          return e ? [{ i, text: e }] : [];
        })
      : [],
  );
  const prediction = $derived(card.type === 'choice' ? null : predictionText(card));
</script>

<CardShell
  {globalKeys}
  {card}
  {index}
  {total}
  state={gradeState}
  {result}
  {box}
  {prompt}
  showUseCase={card.type === 'reduce' ? false : showUseCase}
  canSubmit={selected.length > 0}
  {onshortcut}
  onsubmit={submit}
  {onnext}
  {onprev}
  {onabort}
  {onhint}
>
  {#if card.type === 'reduce'}
    <div class="problem" data-testid="problem">
      <h2 class="sub">問題</h2>
      <InlineMarkdown text={card.problem} block />
    </div>
  {/if}
  {#if card.type !== 'reduce' && card.code}
    <CodeBlock code={card.code} />
  {/if}
  {#if card.type !== 'choice' && card.constraints?.length}
    <div class="constraints">
      <h2 class="sub">制約</h2>
      <ul>
        {#each card.constraints as c (c.name)}
          <li class="num">{constraintText(c)}</li>
        {/each}
      </ul>
    </div>
  {/if}

  <fieldset bind:this={fieldset} class="answer" disabled={graded} data-testid="answer">
    <legend>{multiple ? '当てはまるものをすべて選んでください' : '1 つ選んでください'}</legend>
    <ol class="choices" role="list">
      {#each shownItems as item, n (item.index)}
        {@const isSel = selected.includes(item.index)}
        <li>
          <label
            class="choice"
            class:selected={isSel}
            class:correct={graded && item.correct}
            class:accepted={graded && item.accepted}
            class:chosen-wrong={graded && isSel && !item.correct && !item.accepted}
            data-testid="choice-{item.index}"
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name="{uid}-choice"
              value={item.index}
              checked={isSel}
              onchange={() => choose(item.index)}
            />
            <kbd aria-hidden="true">{n + 1}</kbd>
            <span class="label">
              {#if item.markdown}<InlineMarkdown text={item.label} />{:else}{item.label}{/if}
            </span>
            {#if graded && item.correct}
              <span class="mark ok"><span aria-hidden="true">✓</span> 正解</span>
            {:else if graded && item.accepted}
              <span class="mark ok"><span aria-hidden="true">✓</span> 正解（許容）</span>
            {:else if graded && isSel}
              <span class="mark ng"><span aria-hidden="true">✕</span> 選んだ答え</span>
            {/if}
          </label>
        </li>
      {/each}
    </ol>
  </fieldset>

  {#snippet feedback()}
    {#if card.type === 'reduce'}
      <p class="reason" data-testid="reason"><span class="eyebrow">理由</span> {card.reason}</p>
      {#if card.useCase}
        <p class="use-case" data-testid="use-case">
          <span class="eyebrow">こんなとき</span>
          {card.useCase}
        </p>
      {/if}
    {/if}
    {#if prediction}
      <p class="prediction" data-testid="prediction">
        <span class="eyebrow">制約からの見積もり</span>
        {prediction}
      </p>
    {/if}
    {#each chosenExplanations as e (e.i)}
      <p class="choice-explanation" data-testid="choice-explanation">
        <span class="eyebrow"
          >{items[e.i]?.markdown ? '選んだ答え' : (items[e.i]?.label ?? '')}</span
        >
        <InlineMarkdown text={e.text} />
      </p>
    {/each}
  {/snippet}
</CardShell>

<style>
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .problem {
    padding: var(--space-3) var(--space-4);
    border-left: 3px solid var(--border-strong);
    background: var(--canvas);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  }
  .constraints ul {
    margin: 0;
    padding-left: var(--space-5);
  }
  .answer {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
  }
  .answer legend {
    font-size: var(--fs-sm);
    font-weight: var(--fw-medium);
    margin-bottom: var(--space-2);
    padding: 0;
  }
  .choices {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin: 0;
  }
  .choice {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: var(--tap-min);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    font-weight: var(--fw-regular);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease-out);
  }
  .choice:hover {
    background: var(--canvas);
  }
  .choice.selected {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .choice.correct,
  .choice.accepted {
    border-color: var(--ok-text);
    box-shadow: 0 0 0 1px var(--ok-text);
  }
  .choice.accepted {
    border-style: dashed;
  }
  .choice.chosen-wrong {
    border-color: var(--err-text);
    border-style: dashed;
  }
  .choice:has(input:focus-visible) {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: var(--focus-offset);
  }
  .choice input:focus-visible {
    outline: none;
  }
  .choice kbd {
    flex: none;
  }
  .label {
    flex: 1;
    min-width: 0;
  }
  .mark {
    flex: none;
    font-size: var(--fs-sm);
    font-weight: var(--fw-bold);
  }
  .mark.ok {
    color: var(--ok-text);
  }
  .mark.ng {
    color: var(--err-text);
  }
  fieldset:disabled .choice {
    cursor: default;
  }
  fieldset:disabled .choice:hover {
    background: var(--surface);
  }
  .reason,
  .use-case,
  .prediction,
  .choice-explanation {
    margin: 0;
  }
  .eyebrow {
    margin-right: var(--space-2);
  }
</style>
